
import React, { useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface MicrosoftSignInButtonProps {
  loading: boolean;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
}

// Function to generate a random string for PKCE
function generateRandomString(length: number): string {
  const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
  let result = '';
  for (let i = 0; i < length; i++) {
    result += charset.charAt(Math.floor(Math.random() * charset.length));
  }
  return result;
}

// Function to hash the code verifier to create the code challenge
async function sha256(plain: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(plain);
  const hash = await crypto.subtle.digest('SHA-256', data);
  return btoa(String.fromCharCode(...new Uint8Array(hash)))
    .replace(/=/g, '')
    .replace(/\+/g, '-')
    .replace(/\//g, '_');
}

const MicrosoftSignInButton = ({ 
  loading, 
  setLoading, 
  setError 
}: MicrosoftSignInButtonProps) => {
  const { toast } = useToast();
  
  useEffect(() => {
    // Check if we're handling a redirect with a code
    const handleAuthRedirect = async () => {
      // This is handled by Supabase's detectSessionInUrl option
      console.log('Checking for auth redirect...');
    };
    
    handleAuthRedirect();
  }, []);
  
  const handleMicrosoftSignIn = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Dynamically get the current domain instead of hardcoding
      const currentOrigin = window.location.origin;
      console.log('Current origin:', currentOrigin);
      
      const redirectUrl = `${currentOrigin}/auth/callback`;
      console.log('Using redirect URL:', redirectUrl);
      
      // The tenant ID for your Azure AD app
      const tenantId = '1070d4e4-18d9-48d8-9b80-822feab5f7f5';
      console.log('Using tenant ID:', tenantId);
      
      // Generate and store the code verifier (Supabase handles this automatically)
      console.log('Starting Microsoft OAuth flow with PKCE...');
      
      // Use Supabase's built-in OAuth flow
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'azure',
        options: {
          redirectTo: redirectUrl,
          queryParams: {
            // Specify the tenant ID 
            tenant_id: tenantId,
            // Include prompt to ensure fresh login
            prompt: 'login',
          },
          scopes: 'openid email profile offline_access',
        },
      });
      
      console.log('Microsoft auth response:', { data, error });
      
      if (error) throw error;
    } catch (error: any) {
      console.error('Microsoft auth error:', error.message);
      setError(error.message);
      toast({
        title: "Authentication Error",
        description: "There was a problem connecting to Microsoft. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button 
      variant="outline" 
      className="w-full flex items-center justify-center gap-2 h-10"
      onClick={handleMicrosoftSignIn}
      disabled={loading}
    >
      <svg 
        xmlns="http://www.w3.org/2000/svg" 
        width="20" 
        height="20" 
        viewBox="0 0 23 23"
        className="mr-2"
      >
        <path fill="#f3f3f3" d="M0 0h23v23H0z"/>
        <path fill="#f35325" d="M1 1h10v10H1z"/>
        <path fill="#81bc06" d="M12 1h10v10H12z"/>
        <path fill="#05a6f0" d="M1 12h10v10H1z"/>
        <path fill="#ffba08" d="M12 12h10v10H12z"/>
      </svg>
      Sign in with Microsoft
    </Button>
  );
};

export default MicrosoftSignInButton;
